import { useEffect } from 'react';
import clamp from 'lodash/clamp';
import { AUTOSCROLL_THRESHOLD_WIDTH, FRAME_WIDTH } from '../../constants';
import type { ApplyFramePositionT, OnFrameChangeT } from '../../types';
import { getFrameIndex } from '../../utils/get-frame-index';
type UseDragHandleOnMouseDownPropsT = {
  isDraggingDisabled: boolean;
  applyFramePosition: ApplyFramePositionT;
  lastFrameRef: React.MutableRefObject<number>;
  markerHandleRef: React.RefObject<HTMLDivElement>;
  onFrameChangeRef: React.RefObject<OnFrameChangeT>;
  gridContainer: HTMLDivElement | null;
  containerRef: React.RefObject<HTMLDivElement>;
  maxFrames: number;
};
export function useDragHandleOnMouseDown({
  isDraggingDisabled,
  applyFramePosition,
  lastFrameRef,
  markerHandleRef,
  onFrameChangeRef,
  gridContainer,
  containerRef,
  maxFrames
}: UseDragHandleOnMouseDownPropsT) {
  useEffect(() => {
    const markerHandlerNode = markerHandleRef.current;
    const handleMouseDown = (e: MouseEvent) => {
      if (isDraggingDisabled) {
        return;
      }
      e.preventDefault();
      e.stopPropagation();
      const handleMouseMove = (e: MouseEvent) => {
        const gridContainerNode = gridContainer;
        if (!gridContainerNode) {
          return;
        }
        const rect = containerRef.current?.parentElement?.getBoundingClientRect();
        if (!rect) {
          return;
        }
        const isDraggingInsideGridContainer = e.clientX >= rect.left && e.clientX <= rect.right;
        let xPosition = e.clientX - rect.left; // 10 - 0 = 10

        const currentScrollLeft = gridContainerNode.scrollLeft;
        if (isDraggingInsideGridContainer) {
          const maxScrollLeft = gridContainerNode.scrollWidth - rect.width;
          if (xPosition < AUTOSCROLL_THRESHOLD_WIDTH && currentScrollLeft > 0) {
            // Automatically scroll the grid container to left

            gridContainerNode.scrollLeft = Math.max(0, currentScrollLeft - FRAME_WIDTH);
          } else if (xPosition > rect.width - AUTOSCROLL_THRESHOLD_WIDTH && currentScrollLeft < maxScrollLeft) {
            // Automatically scroll the grid container to right
            gridContainerNode.scrollLeft = Math.min(maxScrollLeft, currentScrollLeft + FRAME_WIDTH);
          }
        }
        const updatedScrollLeft = gridContainerNode.scrollLeft;
        const frameIndex = getFrameIndex({
          scrollLeft: updatedScrollLeft,
          mouseXPosition: xPosition,
          frameWidth: FRAME_WIDTH,
          maxFrames
        });
        const leftMostFrameIndex = getFrameIndex({
          scrollLeft: updatedScrollLeft,
          mouseXPosition: 0,
          frameWidth: FRAME_WIDTH,
          maxFrames
        });
        const rightMostFrameIndex = getFrameIndex({
          scrollLeft: updatedScrollLeft,
          mouseXPosition: gridContainerNode.clientWidth - FRAME_WIDTH,
          frameWidth: FRAME_WIDTH,
          maxFrames
        });
        const clampedFrameIndex = clamp(frameIndex, leftMostFrameIndex, rightMostFrameIndex);
        if (clampedFrameIndex !== lastFrameRef.current) {
          lastFrameRef.current = clampedFrameIndex;
          onFrameChangeRef.current?.(lastFrameRef.current);
          applyFramePosition(updatedScrollLeft);
        }
      };
      const handleMouseUp = () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
        onFrameChangeRef.current?.(lastFrameRef.current || 0);
      };
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    };
    markerHandlerNode?.addEventListener('mousedown', handleMouseDown);
    return () => markerHandlerNode?.removeEventListener('mousedown', handleMouseDown);
  }, [isDraggingDisabled, applyFramePosition, markerHandleRef, lastFrameRef, onFrameChangeRef, maxFrames, gridContainer, containerRef]);
}